import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

function pickLocale(obj = {}, lang = "fa") {
  if (typeof obj === "string") return obj;
  if (!obj || typeof obj !== "object") return "";
  const code = lang?.startsWith("fa") ? "fa" : "en";
  return obj[code] || obj[code === "fa" ? "en" : "fa"] || Object.values(obj).find(Boolean) || "";
}

const ExamSectionModal = ({
  open,
  onClose,
  examTitle,
  sections,
  fallbackSectionKey = "main",
  onStart,
}) => {
  const { t, i18n } = useTranslation();

  const normalizedSections = useMemo(() => {
    if (Array.isArray(sections) && sections.length > 0) {
      return sections.map((s, idx) => ({
        key: s.key || `s${idx + 1}`,
        title: pickLocale(s.title, i18n.language) || `${t("exam_card.section")} ${idx + 1}`,
        order: idx + 1,
      }));
    }
    return [{ key: fallbackSectionKey, title: t("exam_card.section_default"), order: 1 }];
  }, [sections, fallbackSectionKey, i18n.language, t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* بک‌درُپ */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* مدال وسط صفحه */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 sm:mx-0 p-6 animate-fadeIn z-10">
        {/* هدر */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{examTitle}</h3>
            <p className="text-xs text-gray-500">{t("exam_card.select_section")}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* بدنه مدال */}
        <div className="max-h-[60vh] overflow-y-auto space-y-3">
          {normalizedSections.map((s) => (
            <div
              key={s.key}
              className="border border-gray-100 rounded-xl p-3 flex items-center justify-between hover:shadow-sm transition"
            >
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center text-xs font-bold">
                  {s.order}
                </span>
                <div className="text-sm font-semibold text-gray-800">{s.title}</div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onStart("test", s.key)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition"
                >
                  {t("exam_card.start_real_test")}
                </button>
                <button
                  onClick={() => onStart("practice", s.key)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition"
                >
                  {t("exam_card.start_practice")}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* فوتر */}
        <div className="mt-4 text-[11px] text-gray-500 text-center">
          {t("exam_card.choose_mode_hint")}
        </div>
      </div>
    </div>
  );
};

ExamSectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  examTitle: PropTypes.string.isRequired,
  sections: PropTypes.array,
  fallbackSectionKey: PropTypes.string,
  onStart: PropTypes.func.isRequired,
};

export default ExamSectionModal;
